import { RuleGroupsEditor } from "@/components/apply-to/RuleGroupsEditor.tsx";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import { canPreviewWorkflowRuleGroups } from "@/pages/workflows/edit/actions/workflow-rule-groups.ts";
import { InputWithVariableInsert } from "@/components/input-with-variable-insert/InputWithVariableInsert.tsx";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import { getWorkflowPlaceholderOptions } from "@/components/rich-editor/rich-editor.types.ts";
import { MeetingCalendarField } from "@/pages/workflows/edit/actions/meeting/MeetingCalendarField.tsx";
import { MeetingDateField } from "@/pages/workflows/edit/actions/meeting/MeetingDateField.tsx";
import { MeetingExternalEmailsField } from "@/pages/workflows/edit/actions/meeting/MeetingExternalEmailsField.tsx";
import { MeetingOrganizerField } from "@/pages/workflows/edit/actions/meeting/MeetingOrganizerField.tsx";
import { MeetingTimeField } from "@/pages/workflows/edit/actions/meeting/MeetingTimeField.tsx";
import { MeetingFormValues } from "@/pages/workflows/edit/actions/meeting/workflow-meeting.schema.ts";
import { useRuleConditionTypes } from "@/queries/use-rules.query.ts";
import { ApiCalendar } from "@/types/calendar.types.ts";
import { ApiEventVisibility } from "@/types/event.types.ts";
import { ApiWorkflowEventTypes } from "@/types/workflows/workflow.base-enum.types.ts";
import { Input, Select, SelectItem, Stack, Switch, Title } from "@skailer-com/uikit";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

interface MeetingActionTabProps {
  control: Control<MeetingFormValues>;
  register: UseFormRegister<MeetingFormValues>;
  errors: FieldErrors<MeetingFormValues>;
  setValue: UseFormSetValue<MeetingFormValues>;
  organizerOptions: SelectItem[];
  onOrganizerSearch: (text: string) => void;
  onOrganizerChange: (value: number | null) => void;
  calendars: ApiCalendar[] | undefined;
  organizerId: number | null;
  onCalendarChange: (value: number | null) => void;
  conferenceAvailable: boolean;
  eventType?: ApiWorkflowEventTypes | null;
  attendeeConditionTypes: ReturnType<typeof useRuleConditionTypes>;
}

export const MeetingActionTab = ({
  control,
  register,
  errors,
  setValue,
  organizerOptions,
  onOrganizerSearch,
  onOrganizerChange,
  calendars,
  organizerId,
  onCalendarChange,
  conferenceAvailable,
  eventType,
  attendeeConditionTypes,
}: MeetingActionTabProps) => {
  const { t } = useTranslation();
  const attendeeGroups = useWatch({ control, name: "attendee_rule_groups" });

  const visibilityOptions: SelectItem[] = [
    { id: ApiEventVisibility.public, title: t("calendar:event_visibility_public") },
    { id: ApiEventVisibility.private, title: t("calendar:event_visibility_private") },
  ];

  return (
    <Stack className={"py-6"} gap={"lg"}>
      <Stack>
        <InputWithVariableInsert
          {...register("meeting_title")}
          error={errors.meeting_title?.message}
          label={t("common:title")}
          required
          variableOptions={getWorkflowPlaceholderOptions(eventType)}
        />

        <MeetingOrganizerField
          control={control}
          organizerOptions={organizerOptions}
          error={errors.organizer_id?.message}
          onSearch={onOrganizerSearch}
          onOrganizerChange={onOrganizerChange}
        />

        <MeetingCalendarField
          control={control}
          calendars={calendars}
          organizerId={organizerId}
          error={errors.calendar_id?.message}
          onCalendarChange={onCalendarChange}
        />
      </Stack>

      <Controller
        control={control}
        name="is_conference"
        render={({ field: { value, onChange } }) => (
          <Switch
            checked={conferenceAvailable && value}
            onChange={onChange}
            disabled={!conferenceAvailable}
            title={t("workflow:action_type_meeting_conference")}
            description={t("workflow:action_type_meeting_conference_description")}
          />
        )}
      />
      <Stack>
        <Controller
          control={control}
          name="visibility"
          render={({ field: { value, onChange } }) => (
            <Select
              label={t("calendar:event_visibility")}
              emptyTitle={t("common:select")}
              options={visibilityOptions}
              value={value}
              onChange={onChange}
              error={errors.visibility?.message}
            />
          )}
        />

        <MeetingDateField control={control} errors={errors} />
      </Stack>

      <MeetingTimeField control={control} />

      <Stack>
        <Title header={t("calendar:guests")} paddingBottom size={"xs"} />
        <RuleGroupsEditor
          control={control}
          setValue={setValue}
          fieldName={"attendee_rule_groups"}
          place={RuleRegistryPlace.workflowMeetingAttendees}
          error={errors.attendee_rule_groups?.message}
          conditionTypes={attendeeConditionTypes.conditionTypes}
          countEmployees={canPreviewWorkflowRuleGroups(
            attendeeGroups,
            attendeeConditionTypes.conditionTypeInfos
          )}
          variant={"list"}
        />
      </Stack>

      <MeetingExternalEmailsField
        control={control}
        error={errors.external_attendee_emails?.message}
      />

      <Input
        label={t("workflow:action_type_meeting_location")}
        type="text"
        {...register("location")}
        error={errors.location?.message}
      />

      <Stack>
        <Title header={t("common:description")} size={"xs"} paddingBottom />
        <Controller
          control={control}
          name="description"
          render={({ field: { value, onChange } }) => (
            <RichEditor
              value={value || ""}
              onChange={onChange}
              error={errors.description?.message}
              minHeight={100}
              placeholderOptions={getWorkflowPlaceholderOptions(eventType)}
            />
          )}
        />
      </Stack>
    </Stack>
  );
};
